import React, { useEffect } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { omit } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Text from '~/components/core/TextComponents/Text';
import PlusIcon from '~/components/icons/PlusIcon';
import TrashIcon from '~/components/icons/TrashIcon';
import LoadingIndicator from '~/components/LoadingIndicator';
import TextFieldFormikUntyped from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import type { GenericTemplatesMetadataResponse } from '~/server_shared/openapi/data-contracts';
import { SlaRuleCreateRequestSlaActionType } from '~/server_shared/openapi/data-contracts';

import { TEXTS } from '../texts';
import type { SlaRecipientOption } from '../types';

import type { ActionFormValues, SlaActionTypeValue, SlaRuleFormValues } from './formValues';
import { EMPTY_ACTION } from './formValues';
import type { GenericTemplateType } from './validationSchema';
import { genericTemplateType } from './validationSchema';

interface TextFieldFormikProps {
  id: string;
  label: string;
  select?: boolean;
  fullWidth?: boolean;
  multiline?: boolean;
  disabled?: boolean;
  autoSelectSingleOption?: boolean;
  value?: unknown;
  onChange?: (event: { target: { value: unknown } }) => void;
  children?: React.ReactNode;
}
// Assertion over a still-untyped JS component, which the TS rule allows for gradual migration: it
// only narrows the props this file passes, so a wrong prop name or type is still a compile error.
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikProps>;

const ACTION_TYPES: SlaActionTypeValue[] = Object.values(SlaRuleCreateRequestSlaActionType);

const isSlaActionType = (value: unknown): value is SlaActionTypeValue => ACTION_TYPES.some((type) => type === value);

/** The same narrowing the save gate applies to the catalog, so the dialog offers only what a write accepts. */
const isOfferedFor = (option: SlaRecipientOption, actionType: SlaActionTypeValue | ''): boolean =>
  option.action_types.some((type) => type === actionType);

/** Reports whether the message templates of the field at `fieldId` failed to load. */
export type TemplatesLoadFailedChangeHandler = (fieldId: string, isFailed: boolean) => void;

/** The `config_json` keys only a notification row shows. The suggestion executors read `title` as their
 *  own, so neither may outlive the type once the row stops showing it. */
const NOTIFICATION_CONFIG_KEYS = ['title', 'details'];

interface GenericTemplateFieldProps {
  id: string;
  templateType: GenericTemplateType;
  selectedTemplateId: number | '';
  organizationId: number;
  isClaimLevel: boolean;
  disabled: boolean;
  onLoadFailedChange: TemplatesLoadFailedChangeHandler;
}

const GenericTemplateField: React.FC<GenericTemplateFieldProps> = ({
  id,
  templateType,
  selectedTemplateId,
  organizationId,
  isClaimLevel,
  disabled,
  onLoadFailedChange,
}) => {
  // The Communications rule dialog's query minus its sub-org/LOB filters: an SLA rule's scope lives
  // in its card's parameters, and the endpoint reads an absent filter as no filter.
  const { isLoading, isError, data } = useDataFetcher<GenericTemplatesMetadataResponse>(
    `/api/v1/generic_templates/organization/${organizationId}`,
    { params: { template_type: templateType, is_enabled: true, is_claim_level: isClaimLevel } }
  );
  // A failed read keeps the previous request's list, which may be another type's or level's, so the
  // field is replaced by the error and the dialog disables Save rather than sending what it cannot show.
  // The report is withdrawn once a read succeeds or the field leaves the form.
  useEffect(() => {
    if (!isError) {
      return;
    }
    onLoadFailedChange(id, true);
    return () => onLoadFailedChange(id, false);
  }, [id, isError, onLoadFailedChange]);
  const templates = data?.templates_metadata ?? [];
  // A stored template that was since disabled stays listed, rather than rendering a blank field
  // over a value the save still sends.
  const isSelectedUnlisted =
    data !== undefined &&
    selectedTemplateId !== '' &&
    !templates.some((template) => template.id === selectedTemplateId);

  if (isError) {
    return <LoadingIndicator isError />;
  }

  return (
    <TextFieldFormik
      id={id}
      label={TEXTS.GENERIC_TEMPLATE_LABEL}
      select
      fullWidth
      disabled={disabled || isLoading}
      // A type or level change clears the template; auto-picking a lone option would hide that from the admin.
      autoSelectSingleOption={false}
    >
      {templates.map((template) => (
        <MenuItem key={template.id} value={template.id}>
          {template.template_name}
        </MenuItem>
      ))}
      {isSelectedUnlisted ? (
        <MenuItem key={selectedTemplateId} value={selectedTemplateId}>
          {TEXTS.GENERIC_TEMPLATE_NO_LONGER_AVAILABLE(selectedTemplateId)}
        </MenuItem>
      ) : null}
    </TextFieldFormik>
  );
};

interface ActionRowProps {
  path: string;
  action: ActionFormValues;
  position: number;
  isRemovable: boolean;
  onRemove: () => void;
  recipientOptions: SlaRecipientOption[];
  organizationId: number;
  isClaimLevel: boolean;
  onTemplatesLoadFailedChange: TemplatesLoadFailedChangeHandler;
}

const ActionRow: React.FC<ActionRowProps> = ({
  path,
  action,
  position,
  isRemovable,
  onRemove,
  recipientOptions,
  organizationId,
  isClaimLevel,
  onTemplatesLoadFailedChange,
}) => {
  const { setFieldValue, isSubmitting } = useFormikContext<SlaRuleFormValues>();
  const templateType = genericTemplateType(action.action_type);
  const offeredRecipients = recipientOptions.filter((option) => isOfferedFor(option, action.action_type));
  // Same re-admission as the Communications rule dialog: a stored recipient the catalog dropped
  // still shows, instead of a blank field over a value the save still sends.
  const isRecipientUnlisted =
    action.recipient !== '' && !offeredRecipients.some((option) => option.key === action.recipient);

  // Reconciled on the admin's pick rather than by watching values: rows are keyed by index, so
  // removing one shifts the next into its slot, and a watcher would read that as a type change.
  const handleActionTypeChange = ({ target }: { target: { value: unknown } }) => {
    if (!isSlaActionType(target.value)) {
      return;
    }
    const nextType = target.value;
    setFieldValue(`${path}.action_type`, nextType);
    const isRecipientOffered = recipientOptions.some(
      (option) => option.key === action.recipient && isOfferedFor(option, nextType)
    );
    if (action.recipient !== '' && !isRecipientOffered) {
      setFieldValue(`${path}.recipient`, '');
    }
    if (genericTemplateType(nextType) !== templateType) {
      setFieldValue(`${path}.generic_template_id`, '');
    }
    const isLeavingNotification =
      action.action_type === SlaRuleCreateRequestSlaActionType.Notification &&
      nextType !== SlaRuleCreateRequestSlaActionType.Notification;
    if (isLeavingNotification && action.config_json) {
      setFieldValue(`${path}.config_json`, omit(action.config_json, NOTIFICATION_CONFIG_KEYS));
    }
  };

  return (
    <div className="flex items-start gap-8 rounded border border-solid border-slate-200 p-12">
      <div className="grid flex-1 grid-cols-3 gap-12">
        <TextFieldFormik
          id={`${path}.action_type`}
          label={TEXTS.ACTION_TYPE_LABEL}
          select
          fullWidth
          disabled={isSubmitting}
          onChange={handleActionTypeChange}
        >
          {ACTION_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {TEXTS.ACTION_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </TextFieldFormik>
        <TextFieldFormik
          id={`${path}.recipient`}
          label={TEXTS.RECIPIENT_LABEL}
          select
          fullWidth
          disabled={action.action_type === '' || isSubmitting}
          // A type change clears the recipient; auto-picking a lone option would hide that from the admin.
          autoSelectSingleOption={false}
        >
          {offeredRecipients.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.desc}
            </MenuItem>
          ))}
          {isRecipientUnlisted ? (
            <MenuItem key={action.recipient} value={action.recipient}>
              {TEXTS.RECIPIENT_NO_LONGER_AVAILABLE(action.recipient)}
            </MenuItem>
          ) : null}
        </TextFieldFormik>
        {templateType !== null ? (
          <GenericTemplateField
            id={`${path}.generic_template_id`}
            templateType={templateType}
            selectedTemplateId={action.generic_template_id}
            organizationId={organizationId}
            isClaimLevel={isClaimLevel}
            disabled={isSubmitting}
            onLoadFailedChange={onTemplatesLoadFailedChange}
          />
        ) : null}
        {action.action_type === SlaRuleCreateRequestSlaActionType.Notification ? (
          // Nested paths, so config_json keys the dialog does not edit (e.g. priority) are kept.
          <div className="col-span-3 grid grid-cols-2 gap-12">
            <TextFieldFormik
              id={`${path}.config_json.title`}
              label={TEXTS.NOTIFICATION_TITLE_LABEL}
              // A new action's config_json is null, and `''` keeps the input controlled until it is typed into.
              value={action.config_json?.title ?? ''}
              fullWidth
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id={`${path}.config_json.details`}
              label={TEXTS.NOTIFICATION_DETAILS_LABEL}
              value={action.config_json?.details ?? ''}
              fullWidth
              multiline
              disabled={isSubmitting}
            />
          </div>
        ) : null}
      </div>
      <IconButton
        size="small"
        aria-label={TEXTS.REMOVE_ACTION_ARIA_LABEL(position)}
        disabled={!isRemovable || isSubmitting}
        onClick={onRemove}
      >
        <TrashIcon />
      </IconButton>
    </div>
  );
};

interface ActionsEditorProps {
  thresholdIndex: number;
  recipientOptions: SlaRecipientOption[];
  organizationId: number;
  isClaimLevel: boolean;
  onTemplatesLoadFailedChange: TemplatesLoadFailedChangeHandler;
}

const ActionsEditor: React.FC<ActionsEditorProps> = ({
  thresholdIndex,
  recipientOptions,
  organizationId,
  isClaimLevel,
  onTemplatesLoadFailedChange,
}) => {
  const { values, isSubmitting } = useFormikContext<SlaRuleFormValues>();
  const actionsPath = `thresholds.${thresholdIndex}.actions`;
  const { actions } = values.thresholds[thresholdIndex];

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        {TEXTS.ACTIONS_HEADING}
      </Text>
      <FieldArray
        name={actionsPath}
        render={({ push, remove }) => (
          <div className="flex flex-col gap-8">
            {actions.map((action, actionIndex) => (
              <ActionRow
                key={actionIndex}
                path={`${actionsPath}.${actionIndex}`}
                action={action}
                position={actionIndex + 1}
                // The backend requires at least one action per threshold.
                isRemovable={actions.length > 1}
                onRemove={() => remove(actionIndex)}
                recipientOptions={recipientOptions}
                organizationId={organizationId}
                isClaimLevel={isClaimLevel}
                onTemplatesLoadFailedChange={onTemplatesLoadFailedChange}
              />
            ))}
            <div>
              <Button
                color="primary"
                onClick={() => push(EMPTY_ACTION)}
                disabled={isSubmitting}
                startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
              >
                {TEXTS.ADD_ACTION}
              </Button>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default ActionsEditor;
